import { BASE_REST_API_URL } from 'common/constants';
import type { BaseAPIParams } from 'lib/utils/BaseAPIParams';
import type { StrapiApiResponse } from 'api/types/strapiApiResponse';
import type {
  CreateHardwareItemDto,
  HardwareItem,
  SerializedHardwareItem,
  UpdateHardwareItemDto,
} from '@enmon/commons/lib/tools';
import { buildQuery } from '@enmon/commons/lib/tools/utils/buildQuery';
import { BaseApiClient } from './baseApiClient';

export type HardwareItemsFilter = BaseAPIParams<SerializedHardwareItem> & {
  hardwares?: SerializedHardwareItem['id'][];
  hardwareItems?: SerializedHardwareItem['id'][];
};
export type DeleteHardwareItemArg = HardwareItem['id'];
export type CreateHardwareItemArg = CreateHardwareItemDto;
export type UpdateHardwareItemArg = {
  id: HardwareItem['id'];
  values: UpdateHardwareItemDto;
};

export type FindHardwareItemsResult = StrapiApiResponse<SerializedHardwareItem>;
export type DeleteHardwareItemResult = void;
export type CreateHardwareItemResult = SerializedHardwareItem;
export type UpdateHardwareItemResult = SerializedHardwareItem;

export class HardwareItemsApiClient extends BaseApiClient {
  constructor(jwt: string) {
    super('hardware-items', BASE_REST_API_URL, jwt);
  }

  public async find(filter: HardwareItemsFilter, signal: AbortSignal | undefined): Promise<FindHardwareItemsResult> {
    const { fields, hardwareItems, hardwares, page, pageSize, populate, sort } = filter;

    const response = await this.axios.get<FindHardwareItemsResult>('', {
      params: buildQuery<SerializedHardwareItem>({
        sort,
        pagination: {
          page,
          pageSize,
        },
        filters: {
          id: {
            $in: hardwareItems,
          },
          hardware: {
            id: {
              $in: hardwares,
            },
          },
        },
        fields,
        populate,
      }),
      signal,
    });

    return response.data;
  }

  public async create(values: CreateHardwareItemArg): Promise<CreateHardwareItemResult> {
    const response = await this.axios.post<CreateHardwareItemResult>('', { data: values });

    return response.data;
  }

  public async update({ id, values }: UpdateHardwareItemArg): Promise<UpdateHardwareItemResult> {
    const response = await this.axios.put<UpdateHardwareItemResult>(`${id}`, { data: values });

    return response.data;
  }

  public async delete(id: DeleteHardwareItemArg): Promise<DeleteHardwareItemResult> {
    const response = await this.axios.delete<DeleteHardwareItemResult>(`${id}`);

    return response.data;
  }
}
